import SEO from '../components/SEO';
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FiClock, FiUser, FiArrowRight, FiTag } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { fetchAllArticles, getImageSrc, formatMarathiDate } from '../apiService';
import { useLanguage } from '../utils/LanguageContext';

const Articles = () => {
  const { language } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  
  const [searchParams, setSearchParams] = useSearchParams();
  const limit = 12;

  useEffect(() => {
    const page = parseInt(searchParams.get('page')) || 1;
    setCurrentPage(page);
    loadArticles(page);
  }, [searchParams]);

  const loadArticles = async (page = 1) => {
    try {
      setLoading(true);
      const result = await fetchAllArticles(page, limit);
      setArticles(result.articles);
      setTotalPages(result.totalPages);
      setTotal(result.total);
    } catch (err) {
      console.error('Error loading articles:', err);
      setError('Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', page.toString());
    setSearchParams(newParams);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return language === 'MR'
      ? date.toLocaleDateString('mr-IN', { year: 'numeric', month: 'long', day: 'numeric' })
      : date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const truncateText = (text, maxLength = 120) => {
    if (!text) return '';
    if (Array.isArray(text)) {
      const fullText = text.join(' ');
      return fullText.length > maxLength ? fullText.substring(0, maxLength) + '...' : fullText;
    }
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const isNewArticle = (dateString) => {
    const articleDate = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - articleDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 7;
  };

  const ArticleCard = ({ article, index }) => (
    <motion.div
      className="bg-white border-2 border-red-100 rounded-xl shadow-md hover:shadow-xl hover:border-red-300 transition-all duration-300 overflow-hidden group relative"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      whileHover={{ y: -6, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      {isNewArticle(article.date) && (
        <div className="absolute top-3 right-3 z-10">
          <span className="bg-red-800 text-white px-2 py-1 rounded-full text-xs font-semibold shadow-md">
            {language === 'MR' ? 'नवीन' : 'New'}
          </span>
        </div>
      )}

      <div className="relative overflow-hidden h-48">
        <img loading="lazy" 
          src={getImageSrc(article.thumbnail)} 
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="bg-white text-red-800 px-4 py-2 rounded-full font-semibold flex items-center shadow-md">
            <FiArrowRight className="mr-2 w-4 h-4" />
            {language === 'MR' ? 'वाचा' : 'Read'}
          </div>
        </div>
      </div>

      <div className="p-5">
        {article.tags && article.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {article.tags.slice(0, 2).map((tag, i) => (
              <span 
                key={i}
                className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium"
              >
                <FiTag className="w-2 h-2" />
                {tag}
              </span>
            ))}
          </div>
        )}

        <h3 className="text-lg font-bold text-red-800 mb-3 line-clamp-2 group-hover:text-red-600 transition-colors leading-tight">
          {article.title}
        </h3>

        {article.content && article.content[0] && (
          <p className="text-red-600 text-sm line-clamp-3 leading-relaxed mb-4">
            {truncateText(article.content, 120)}
          </p>
        )}

        <div className="flex items-center justify-between text-xs text-red-700 mb-4">
          <div className="flex items-center">
            <FiUser className="mr-1 text-red-600 w-3 h-3" />
            <span className="font-medium truncate">{article.author || 'Admin'}</span>
          </div>
          <div className="flex items-center">
            <FiClock className="mr-1 text-red-600 w-3 h-3" />
            <span>{formatDate(article.date)}</span>
          </div>
        </div>

        <div className="text-center">
          <Link 
            to={`/article/${article.slug || article._id}`}
            className="inline-flex items-center gap-2 bg-red-800 hover:bg-red-900 text-white px-4 py-2 rounded-lg font-medium transition-all duration-300"
          >
            {language === 'MR' ? 'संपूर्ण लेख वाचा' : 'Read Full Article'}
            <FiArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-white pt-20">
      <SEO title="लेख" description="डॉ. राजेंद्र शेंडे यांचे सर्व मराठी लेख वाचा — हवामान बदल, पर्यावरण आणि शाश्वत विकासावर." canonical="/allarticles" />
      <div className="max-w-7xl mx-auto px-4 py-4">
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl font-medium text-red-800 mb-4">
            {language === 'MR' ? 'सर्व मराठी लेख' : 'All Marathi Articles'}
          </h1>
          <div className="w-24 h-1 bg-red-800 mx-auto mb-4" />
          <p className="text-lg text-red-700 max-w-2xl mx-auto">
            {language === 'MR'
              ? 'आमच्या संपूर्ण मराठी लेख संग्रहातून तुमच्या आवडत्या विषयावर वाचा'
              : 'Read about your favorite topics from our complete Marathi articles collection'}
          </p>
        </motion.div>

        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(12)].map((_, index) => (
              <div key={index} className="bg-white border-2 border-red-100 rounded-xl shadow-md overflow-hidden animate-pulse">
                <div className="h-48 bg-gray-300"></div>
                <div className="p-5">
                  <div className="h-4 bg-gray-300 rounded mb-3"></div>
                  <div className="h-4 bg-gray-300 rounded mb-3 w-3/4"></div>
                  <div className="h-3 bg-gray-300 rounded mb-2"></div>
                  <div className="h-3 bg-gray-300 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        )}

        {error && !loading && (
          <div className="text-center py-4">
            <div className="bg-red-50 border-2 border-red-100 rounded-xl p-8 max-w-md mx-auto">
              <div className="text-6xl text-red-800 mb-4">⚠</div>
              <p className="text-red-800 font-semibold mb-4">
                {language === 'MR' ? 'लेख लोड करताना त्रुटी' : 'Error loading articles'}
              </p>
              <button 
                onClick={() => loadArticles(currentPage)}
                className="bg-red-800 hover:bg-red-900 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                {language === 'MR' ? 'पुन्हा प्रयत्न करा' : 'Try Again'}
              </button>
            </div>
          </div>
        )}

        {!loading && !error && articles.length === 0 && (
          <motion.div 
            className="text-center py-4"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-6xl mb-4">📝</div>
            <h3 className="text-2xl font-bold text-red-800 mb-2">
              {language === 'MR' ? 'कोणतेही लेख सापडले नाहीत' : 'No Articles Found'}
            </h3>
            <p className="text-red-600">
              {language === 'MR' ? 'सध्या कोणतेही लेख उपलब्ध नाहीत' : 'No articles are currently available'}
            </p>
          </motion.div>
        )}

        {!loading && !error && articles.length > 0 && (
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
          >
            {articles.map((article, index) => (
              <ArticleCard key={article._id} article={article} index={index} />
            ))}
          </motion.div>
        )}

        {!loading && !error && totalPages > 1 && (
          <motion.div 
            className="flex justify-center items-center gap-2 mt-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <button
              onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 border-2 border-red-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-50 transition-colors text-red-800 font-medium"
            >
              {language === 'MR' ? 'मागील' : 'Previous'}
            </button>
            
            {[...Array(totalPages)].map((_, index) => {
              const page = index + 1;
              const isActive = page === currentPage;
              
              if (
                page === 1 || 
                page === totalPages || 
                (page >= currentPage - 2 && page <= currentPage + 2)
              ) {
                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-4 py-2 rounded-lg transition-colors font-medium ${
                      isActive 
                        ? 'bg-red-800 text-white' 
                        : 'border-2 border-red-300 text-red-800 hover:bg-red-50'
                    }`}
                  >
                    {page}
                  </button>
                );
              } else if (
                page === currentPage - 3 || 
                page === currentPage + 3
              ) {
                return <span key={page} className="px-2 text-red-600">...</span>;
              }
              return null;
            })}
            
            <button
              onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 border-2 border-red-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-50 transition-colors text-red-800 font-medium"
            >
              {language === 'MR' ? 'पुढील' : 'Next'}
            </button>
          </motion.div>
        )}

        {!loading && !error && articles.length > 0 && (
          <motion.div 
            className="text-center mt-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <p className="text-red-700 font-medium">
              {language === 'MR'
                ? `एकूण ${total} लेखांपैकी ${articles.length} दाखवत आहे`
                : `Showing ${articles.length} of ${total} articles`}
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Articles;